import * as React from 'react';
import { Avatar } from '@/components/common/Avatar';
import { useAuth } from '@/hooks/useAuth';
import clsx from 'clsx';
import cameraIcon from '@/assets/images/svg/camera_icon.svg';
import { useProfilePicture } from '../hooks/useProfilePicture';
import { getUserFullName, getUserInitials } from '@/utils/formatters';
import { useEchoStore } from '@/context/EchoContext';

export const ProfileHeader: React.FC = () => {
  const { user } = useAuth();
  const { myEchoes } = useEchoStore();
  const { isUploading, handleUpload } = useProfilePicture();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const displayName = getUserFullName(user);
  const email = user?.email || 'guest@example.com';
  const initials = getUserInitials(user);

  // Determine creator badge: role-based OR has created echoes
  const isCreator =
    user?.role === 'creator' ||
    user?.role === 'echo' ||
    (Array.isArray(myEchoes) && myEchoes.length > 0);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleUpload(e.target.files[0]);
      e.target.value = '';
    }
  };

  return (
    <div className='mb-8 flex w-full flex-col items-start gap-6'>
      <div className='flex flex-row items-center gap-4 md:gap-6'>
        <div className='relative shrink-0'>
          <Avatar
            size={134}
            initials={initials}
            src={user?.profile_picture_url ?? null}
            className={clsx(
              '!h-[80px] !w-[80px] md:!h-[134px] md:!w-[134px]',
              'border-3 border-brand-300 bg-gray-340',
              isUploading && 'opacity-50'
            )}
            textClassName='font-proxima font-bold text-[32px] md:text-[48px] uppercase text-brand-800'
          />

          <input
            type='file'
            ref={fileInputRef}
            className='hidden'
            accept='image/png, image/jpeg, image/webp'
            onChange={onFileChange}
            disabled={isUploading}
          />

          <button
            className='absolute bottom-0 right-0 flex size-6 items-center justify-center rounded-full bg-gray-330 shadow-sm transition-colors hover:bg-gray-300 disabled:opacity-50 md:bottom-1 md:right-1 md:size-8'
            aria-label='Change profile picture'
            type='button'
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <img src={cameraIcon} alt='' className='size-3 md:size-4' />
          </button>
        </div>

        <div className='flex min-w-0 flex-col items-start gap-1'>
          <h2 className='w-full truncate font-sans text-18 font-bold text-dark-100 md:text-account-name'>
            {displayName}
          </h2>
          <p className='w-full truncate font-sans text-14 font-medium text-dark-300 md:text-account-text'>
            {email}
          </p>

          <div
            className={clsx(
              'mt-1 flex items-center justify-center md:mt-2',
              'h-[22px] min-w-[100px] rounded-16 px-3 md:h-[26px] md:min-w-[117px]',
              'bg-fan-badge-gradient backdrop-blur-[44px]',
              'font-sans text-10 font-medium uppercase text-white md:text-12'
            )}
          >
            {isCreator ? 'Creator Account' : 'Fan Account'}
          </div>
        </div>
      </div>
    </div>
  );
};
